import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, AlertCircle, Crown, Zap, Gift, ArrowRight } from 'lucide-react';
import { userPlansApi } from '../services/userPlansApi';
interface UsageCounterProps {
  className?: string;
  showDetails?: boolean;
}

export function UsageCounter({ className = '', showDetails = false }: UsageCounterProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: usageData, isLoading, error } = useQuery({
    queryKey: ['userUsage'],
    queryFn: () => userPlansApi.getUserUsage(),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
    retry: 2
  });

  const usage = usageData?.usage;

  if (isLoading) {
    return (
      <div className={`bg-stone-800/50 border border-stone-700 rounded-xl p-3 ${className}`}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-stone-600 rounded-full animate-pulse"></div>
          <div className="space-y-1 flex-1">
            <div className="h-3 bg-stone-600 rounded animate-pulse"></div>
            <div className="h-2 bg-stone-700 rounded animate-pulse w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !usage) {
    return (
      <div className={`bg-red-950/40 border border-red-800/60 rounded-xl p-3 ${className}`}>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div>
            <p className="text-red-200 font-medium text-sm">Error al cargar uso</p>
            <p className="text-red-300 text-xs">No se pudo obtener la información</p>
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage = (usage.adsFetched / usage.monthlyLimit) * 100;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  const getPlanIcon = (planType: string) => {
    switch (planType) {
      case 'free': return <Gift className="w-5 h-5" />;
      case 'pioneros': return <BarChart3 className="w-5 h-5" />;
      case 'tactico': return <TrendingUp className="w-5 h-5" />;
      case 'conquista': return <Crown className="w-5 h-5" />;
      case 'imperio': return <Zap className="w-5 h-5" />;
      default: return <BarChart3 className="w-5 h-5" />;
    }
  };

  const getPlanColor = (planType: string) => {
    switch (planType) {
      case 'free': return 'text-emerald-400';
      case 'pioneros': return 'text-stone-300';
      case 'tactico': return 'text-blue-400';
      case 'conquista': return 'text-secondary-400';
      case 'imperio': return 'text-amber-400';
      default: return 'text-stone-300';
    }
  };

  const getProgressColor = () => {
    if (isAtLimit) return 'bg-red-500';
    if (isNearLimit) return 'bg-amber-500';
    return 'bg-stone-100';
  };

  return (
    <div className={`bg-stone-800/50 border border-stone-700 rounded-xl p-3 hover:border-stone-600 transition-colors ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 bg-stone-900 border border-stone-600 rounded-full flex items-center justify-center ${getPlanColor(usage.planType)}`}>
            {getPlanIcon(usage.planType)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-stone-100 text-sm">{usage.planName}</h3>
              {isAtLimit && (
                <span className="px-1.5 py-0.5 bg-red-900/60 text-red-200 text-xs rounded-full font-medium">
                  Límite
                </span>
              )}
              {isNearLimit && !isAtLimit && (
                <span className="px-1.5 py-0.5 bg-amber-900/50 text-amber-200 text-xs rounded-full font-medium">
                  Cerca
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500">
              {usage.adsFetched.toLocaleString()} / {usage.monthlyLimit.toLocaleString()} anuncios
            </p>
          </div>
        </div>
        
        {showDetails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-stone-400 hover:text-stone-100 transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="mt-2">
        <div className="flex justify-between text-xs text-stone-500 mb-1">
          <span>Uso mensual</span>
          <span>{usagePercentage.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-stone-700 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      <Link 
        to="/user-plans" 
        className="mt-3 flex items-center justify-center gap-2 text-xs text-secondary-400 hover:text-secondary-300 hover:bg-secondary-900/30 rounded-lg py-1.5 px-2 transition-colors font-medium"
      >
        <Crown className="w-3 h-3" />
        Ver Planes
        <ArrowRight className="w-3 h-3" />
      </Link>

      {isExpanded && showDetails && (
        <div className="mt-4 pt-4 border-t border-stone-700 space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-stone-500">Anuncios restantes</p>
              <p className="font-semibold text-stone-100">{usage.adsRemaining.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-stone-500">Búsquedas realizadas</p>
              <p className="font-semibold text-stone-100">{usage.searchesPerformed.toLocaleString()}</p>
            </div>
          </div>
          <div className="text-xs text-stone-500 space-y-1">
            <p>Próximo reset: {new Date(usage.resetDate).toLocaleDateString('es-ES', { 
              month: 'long', 
              year: 'numeric' 
            })}</p>
            
            {usage.subscriptionExpiration && usage.planType !== 'free' && (
              <div className={`flex items-center gap-1 ${
                usage.subscriptionStatus === 'expired' 
                  ? 'text-red-600' 
                  : new Date(usage.subscriptionExpiration) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                    ? 'text-amber-700'
                    : 'text-stone-500'
              }`}>
                <span>
                  {usage.subscriptionStatus === 'expired' 
                    ? '⚠️ Plan vencido' 
                    : `Plan vence: ${new Date(usage.subscriptionExpiration).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}`
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
